/**
 * Carte de supervision — tout le parc, toutes exploitations confondues.
 *
 * Rendue en SVG au-dessus d'un fond de tuiles OPTIONNEL (voir `lib/geo.ts` pour le pourquoi).
 * Sans fond configuré, la carte reste utile : ce qu'on y cherche, ce sont les grappes rouges et
 * les appareils isolés, pas le nom des routes.
 *
 * Interactions : glisser pour déplacer, molette pour zoomer (autour du curseur), clic sur un
 * marqueur pour ouvrir la fiche, clic sur une grappe pour zoomer dessus. Le zoom molette est
 * câblé en écouteur natif non passif — un écouteur React ne peut pas annuler le défilement.
 *
 * BA_00010 — « le déplacement et le zoom ne sont pas fiables ; le premier déplacement marche,
 * pas les suivants ; impossible de cliquer sur un objet ». Deux causes :
 *   1. le conteneur CAPTURAIT le pointeur dès l'appui, avant tout déplacement : le clic sur un
 *      marqueur ou sur les boutons + / − était intercepté par la carte et n'atteignait jamais
 *      sa cible ;
 *   2. le glissement recalculait la vue à partir de `vue` lue dans le rendu précédent : sur des
 *      mouvements rapides, plusieurs évènements partaient de la même valeur périmée, et la carte
 *      sautait.
 * La capture n'est désormais prise qu'au-delà d'un seuil de 4 px, jamais sur un bouton ni un
 * marqueur, et chaque mouvement part de la vue COURANTE.
 *
 * BA_00012 — les appareils proches à l'écran sont regroupés en une grappe qui affiche leur
 * NOMBRE, colorée selon le pire état qu'elle contient.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { COULEUR_PAR_ETAT_APPAREIL, EtatAppareil, couleurMarqueur } from '@genviz/shared/enums';

import { TUILES_ATTRIBUTION, TUILES_URL } from '../config';
import { LIBELLE_ETAT_APPAREIL } from '../i18n/fr';
import { borner, classes } from '../lib/divers';
import {
  ECHELLE_MAX,
  ECHELLE_MIN,
  cadrer,
  empriseDe,
  tuilesVisibles,
  urlTuile,
  versEcran,
  type Vue,
} from '../lib/geo';
import { useTaille } from '../lib/useTaille';
import type { MarqueurSupervision } from '../api/types';
import { HEX_PAR_COULEUR } from './Badges';

const ETATS_ORDONNES: EtatAppareil[] = [
  EtatAppareil.NOMINAL,
  EtatAppareil.ATTENTION,
  EtatAppareil.CRITIQUE,
  EtatAppareil.ETEINT,
];

/** Distance à l'écran en deçà de laquelle deux marqueurs sont regroupés. */
const RAYON_GRAPPE_PX = 22;
/** Déplacement minimal avant qu'un appui devienne un glissement (sinon, c'est un clic). */
const SEUIL_GLISSEMENT_PX = 4;

/** Gravité d'une couleur de marqueur : la grappe prend celle de son pire appareil. */
const GRAVITE: Record<string, number> = { ROUGE: 5, ORANGE: 4, GRIS: 3, BLEU: 2, VERT: 1, NOIR: 0 };

/** Rayon d'un marqueur, agrandi quand l'appareil porte des alertes actives. */
function rayon(appareil: MarqueurSupervision, selectionne: boolean): number {
  const base = appareil.alertes_actives > 0 ? 9 : 7;
  return selectionne ? base + 3 : base;
}

export function CarteGeo({
  appareils,
  selectionne = null,
  surSelection,
}: {
  appareils: readonly MarqueurSupervision[];
  selectionne?: string | null;
  surSelection: (appareil: MarqueurSupervision) => void;
}): JSX.Element {
  const [conteneur, taille] = useTaille<HTMLDivElement>();
  const [vue, setVue] = useState<Vue | null>(null);
  const [saisie, setSaisie] = useState(false);
  const appui = useRef<{ x: number; y: number; id: number; glisse: boolean } | null>(null);
  const cleCadrage = useRef<string>('');

  const emprise = useMemo(() => empriseDe(appareils), [appareils]);

  const recadrer = useCallback(() => {
    if (!emprise || taille.largeur === 0 || taille.hauteur === 0) return;
    setVue(cadrer(emprise, taille.largeur, taille.hauteur));
  }, [emprise, taille.largeur, taille.hauteur]);

  // Cadrage automatique au premier rendu et à chaque changement de jeu d'appareils (filtre),
  // mais JAMAIS pendant que l'opérateur navigue : un recadrage intempestif lui ferait perdre
  // l'appareil qu'il était en train de regarder.
  useEffect(() => {
    if (!emprise || taille.largeur === 0) return;
    const cle = `${appareils.length}:${emprise.nord}:${emprise.sud}:${emprise.est}:${emprise.ouest}`;
    if (cle === cleCadrage.current && vue !== null) return;
    cleCadrage.current = cle;
    setVue(cadrer(emprise, taille.largeur, taille.hauteur));
  }, [appareils.length, emprise, taille.largeur, taille.hauteur, vue]);

  // Zoom molette : écouteur natif, non passif, sinon `preventDefault` est ignoré.
  useEffect(() => {
    const element = conteneur.current;
    if (!element) return undefined;

    const surMolette = (e: WheelEvent): void => {
      e.preventDefault();
      const rect = element.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      setVue((precedente) => {
        if (!precedente) return precedente;
        const facteur = Math.exp(-e.deltaY * 0.0015);
        const echelle = borner(precedente.echelle * facteur, ECHELLE_MIN, ECHELLE_MAX);
        // Zoom AUTOUR DU CURSEUR : le point sous la souris reste sous la souris.
        const mx = precedente.centre.x + (px - rect.width / 2) / precedente.echelle;
        const my = precedente.centre.y + (py - rect.height / 2) / precedente.echelle;
        return {
          echelle,
          centre: { x: mx - (px - rect.width / 2) / echelle, y: my - (py - rect.height / 2) / echelle },
        };
      });
    };

    element.addEventListener('wheel', surMolette, { passive: false });
    return () => {
      element.removeEventListener('wheel', surMolette);
    };
  }, [conteneur]);

  const zoomer = (facteur: number): void => {
    setVue((precedente) =>
      precedente
        ? {
            centre: precedente.centre,
            echelle: borner(precedente.echelle * facteur, ECHELLE_MIN, ECHELLE_MAX),
          }
        : precedente,
    );
  };

  const tuiles =
    vue && TUILES_URL ? tuilesVisibles(vue, taille.largeur, taille.hauteur) : [];

  const positions =
    vue && taille.largeur > 0
      ? appareils.map((a) => ({
          appareil: a,
          ecran: versEcran({ lat: a.lat, lon: a.lon }, vue, taille.largeur, taille.hauteur),
        }))
      : [];

  /** Regroupement glouton à l'écran : chaque marqueur rejoint la première grappe assez proche. */
  const grappes: { x: number; y: number; membres: typeof positions }[] = [];
  for (const position of positions) {
    const cible = grappes.find(
      (g) => Math.hypot(g.x - position.ecran.x, g.y - position.ecran.y) < RAYON_GRAPPE_PX,
    );
    if (cible) {
      cible.membres.push(position);
      const n = cible.membres.length;
      cible.x += (position.ecran.x - cible.x) / n;
      cible.y += (position.ecran.y - cible.y) / n;
    } else {
      grappes.push({ x: position.ecran.x, y: position.ecran.y, membres: [position] });
    }
  }

  /** Zoom centré sur un point de l'écran (clic sur une grappe). */
  const zoomerSur = (sx: number, sy: number, facteur: number): void => {
    setVue((precedente) => {
      if (!precedente) return precedente;
      const cx = precedente.centre.x + (sx - taille.largeur / 2) / precedente.echelle;
      const cy = precedente.centre.y + (sy - taille.hauteur / 2) / precedente.echelle;
      return { centre: { x: cx, y: cy }, echelle: borner(precedente.echelle * facteur, ECHELLE_MIN, ECHELLE_MAX) };
    });
  };

  return (
    <div
      className={classes('gv-carte-geo', saisie && 'gv-carte-geo--saisie')}
      ref={conteneur}
      onPointerDown={(e) => {
        // Ni les boutons ni les marqueurs ne démarrent un glissement : leur clic doit passer.
        if ((e.target as Element).closest('button, .gv-marqueur, .gv-grappe')) return;
        appui.current = { x: e.clientX, y: e.clientY, id: e.pointerId, glisse: false };
      }}
      onPointerMove={(e) => {
        const a = appui.current;
        if (!a) return;
        const dx = e.clientX - a.x;
        const dy = e.clientY - a.y;
        if (!a.glisse) {
          if (Math.hypot(dx, dy) < SEUIL_GLISSEMENT_PX) return;
          a.glisse = true;
          setSaisie(true);
          e.currentTarget.setPointerCapture(a.id);
        }
        a.x = e.clientX;
        a.y = e.clientY;
        // Mise à jour FONCTIONNELLE : chaque mouvement part de la vue courante.
        setVue((precedente) =>
          precedente
            ? {
                echelle: precedente.echelle,
                centre: {
                  x: precedente.centre.x - dx / precedente.echelle,
                  y: precedente.centre.y - dy / precedente.echelle,
                },
              }
            : precedente,
        );
      }}
      onPointerUp={(e) => {
        if (appui.current?.glisse && e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId);
        }
        appui.current = null;
        setSaisie(false);
      }}
      onPointerCancel={() => {
        appui.current = null;
        setSaisie(false);
      }}
    >
      {tuiles.map((t) => (
        <img
          key={`${t.z}-${t.x}-${t.y}`}
          src={urlTuile(TUILES_URL as string, t)}
          alt=""
          aria-hidden="true"
          draggable={false}
          style={{
            position: 'absolute',
            left: t.gauche,
            top: t.haut,
            width: t.taille,
            height: t.taille,
            userSelect: 'none',
          }}
        />
      ))}

      <svg
        width={taille.largeur}
        height={taille.hauteur}
        style={{ position: 'absolute', inset: 0 }}
        role="img"
        aria-label={`Carte de supervision — ${appareils.length} appareil${appareils.length > 1 ? 's' : ''} localisé${appareils.length > 1 ? 's' : ''}`}
      >
        {TUILES_URL === null ? (
          // Graticule discret : donne une échelle visuelle en l'absence de fond de carte.
          <g className="gv-graphe__grille">
            {Array.from({ length: Math.ceil(taille.largeur / 80) }, (_, i) => (
              <line key={`v${i}`} x1={i * 80} x2={i * 80} y1={0} y2={taille.hauteur} />
            ))}
            {Array.from({ length: Math.ceil(taille.hauteur / 80) }, (_, i) => (
              <line key={`h${i}`} x1={0} x2={taille.largeur} y1={i * 80} y2={i * 80} />
            ))}
          </g>
        ) : null}

        {grappes.map((grappe) => {
          if (grappe.membres.length > 1) {
            const pire = grappe.membres
              .map((m) => couleurMarqueur(m.appareil.statut_metier, m.appareil.connectivite))
              .sort((c1, c2) => (GRAVITE[c2] ?? 0) - (GRAVITE[c1] ?? 0))[0]!;
            const n = grappe.membres.length;
            const r = Math.min(12 + Math.log2(n) * 3, 24);
            return (
              <g
                key={`grappe-${grappe.membres.map((m) => m.appareil.id).join('-')}`}
                className="gv-grappe"
                style={{ cursor: 'zoom-in' }}
                onClick={(e) => {
                  e.stopPropagation();
                  zoomerSur(grappe.x, grappe.y, 3);
                }}
                role="button"
                tabIndex={0}
                aria-label={`${n} appareils regroupés — cliquer pour zoomer`}
              >
                <circle cx={grappe.x} cy={grappe.y} r={r + 4} fill={HEX_PAR_COULEUR[pire]} opacity={0.25} />
                <circle cx={grappe.x} cy={grappe.y} r={r} fill={HEX_PAR_COULEUR[pire]} stroke="#fff" strokeWidth={2} />
                <text
                  x={grappe.x}
                  y={grappe.y + 4}
                  textAnchor="middle"
                  style={{ fontWeight: 700, fontSize: 12, fill: '#fff', pointerEvents: 'none' }}
                >
                  {n}
                </text>
                <title>{`${n} appareils : ${grappe.membres.map((m) => m.appareil.nom).join(', ')}`}</title>
              </g>
            );
          }
          const { appareil, ecran } = grappe.membres[0]!;
          const estSelectionne = appareil.id === selectionne;
          return (
            <g key={appareil.id}>
              <circle
                className={classes('gv-marqueur', estSelectionne && 'gv-marqueur--selectionne')}
                cx={ecran.x}
                cy={ecran.y}
                r={rayon(appareil, estSelectionne)}
                // La connectivité entre dans la couleur : hors ligne, le marqueur est gris,
                // quel que soit l'état métier (FR_00053).
                fill={HEX_PAR_COULEUR[couleurMarqueur(appareil.statut_metier, appareil.connectivite)]}
                onClick={(e) => {
                  e.stopPropagation();
                  surSelection(appareil);
                }}
                tabIndex={0}
                role="button"
                aria-label={`${appareil.nom} — ${appareil.exploitation.nom} — ${LIBELLE_ETAT_APPAREIL[appareil.statut_metier]}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    surSelection(appareil);
                  }
                }}
              >
                <title>
                  {appareil.nom} · {appareil.exploitation.nom} ·{' '}
                  {LIBELLE_ETAT_APPAREIL[appareil.statut_metier]}
                </title>
              </circle>
              {estSelectionne ? (
                <text
                  x={ecran.x}
                  y={ecran.y - rayon(appareil, true) - 6}
                  textAnchor="middle"
                  className="gv-graphe__axe-texte"
                  style={{ fontWeight: 700, fill: 'var(--gv-texte)' }}
                >
                  {appareil.nom}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>

      <div className="gv-carte-geo__zoom gv-sans-impression">
        <button type="button" aria-label="Zoomer" onClick={() => { zoomer(1.6); }}>
          +
        </button>
        <button type="button" aria-label="Dézoomer" onClick={() => { zoomer(1 / 1.6); }}>
          −
        </button>
        <button
          type="button"
          aria-label="Recadrer sur le parc"
          title="Recadrer sur le parc"
          onClick={recadrer}
          style={{ fontSize: 14 }}
        >
          ⤢
        </button>
      </div>

      <div className="gv-carte-geo__legende">
        {ETATS_ORDONNES.map((etat) => (
          <span key={etat} className="gv-ligne" style={{ gap: 6 }}>
            <span
              className="gv-badge__point"
              style={{ background: HEX_PAR_COULEUR[COULEUR_PAR_ETAT_APPAREIL[etat]] }}
              aria-hidden="true"
            />
            {LIBELLE_ETAT_APPAREIL[etat]}
          </span>
        ))}
        {/* Le gris figure sur la carte (appareil hors ligne, FR_00053) : il doit figurer ici. */}
        <span className="gv-ligne" style={{ gap: 6 }}>
          <span className="gv-badge__point" style={{ background: HEX_PAR_COULEUR.GRIS }} aria-hidden="true" />
          Hors ligne
        </span>
      </div>

      {TUILES_URL && TUILES_ATTRIBUTION ? (
        <span className="gv-carte-geo__attribution">{TUILES_ATTRIBUTION}</span>
      ) : null}
    </div>
  );
}
